import { useMemo } from "react";
import { asNumber, formatValue } from "../../lib/kernelFormat.js";
import { boundedLimit, divergingScale } from "../../lib/decisionScales.js";
import { sanitizeRenderColor } from "../../lib/renderSafety.js";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { KernelEmpty } from "./shared.jsx";

export const kind = "deltaBars";

function toneFor(delta, mode) {
    if (!["up", "down"].includes(mode) || delta === 0) return "var(--muted)";
    const favorable = mode === "down" ? delta < 0 : delta > 0;
    return favorable ? "var(--green)" : "var(--red, #c0392b)";
}

export function Component({ spec, data, fillHeight = false, onFocus }) {
    const maxItems = boundedLimit(spec.maxItems, 10, 20);
    const rows = useMemo(() => {
        const shaped = (Array.isArray(data?.steps) ? data.steps : Array.isArray(data) ? data : [])
            .map((row) => ({ label: String(row?.label ?? "Unlabelled"), delta: asNumber(row?.delta ?? row?.value) }))
            .filter((row) => row.delta != null)
            .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
        const kept = shaped.slice(0, maxItems);
        const tail = shaped.slice(maxItems);
        if (tail.length) kept.push({ label: `Other (${tail.length})`, delta: tail.reduce((sum, row) => sum + row.delta, 0), folded: true });
        return kept;
    }, [data, maxItems]);
    if (!rows.length) return <KernelEmpty message={spec.empty || "No changes to show"} />;

    const scale = divergingScale(rows.map((row) => row.delta));
    const format = spec.format || "number";
    const mode = ["up", "down", "neutral"].includes(spec.favorable) ? spec.favorable : "neutral";
    const start = asNumber(data?.start?.value);
    const end = asNumber(data?.end?.value);
    const focused = spec.focusValue == null ? null : String(spec.focusValue);

    return (
        <div className={fillHeight ? "flex h-full min-h-0 flex-col" : ""}>
            {start != null && end != null ? (
                <div className="mb-[7px] text-[11px] text-[color:var(--muted)]">
                    {data?.start?.label || "Start"} <span className="font-mono text-[color:var(--ink)]">{formatValue(start, format)}</span>
                    {" -> "}
                    {data?.end?.label || "End"} <span className="font-mono text-[color:var(--ink)]">{formatValue(end, format)}</span>
                </div>
            ) : null}
            <div>
                {rows.map((row, index) => {
                    const positive = row.delta > 0;
                    const width = scale.widthPercent(row.delta);
                    const selected = focused != null && row.label === focused;
                    const dimmed = focused != null && !selected;
                    const clickable = Boolean(onFocus && !row.folded);
                    const tone = toneFor(row.delta, mode);
                    return (
                        <div
                            key={`${row.label}-${index}`}
                            role={clickable ? "button" : undefined}
                            tabIndex={clickable ? 0 : undefined}
                            aria-pressed={clickable ? selected : undefined}
                            onClick={clickable ? () => onFocus(spec.groupField || "label", selected ? null : row.label) : undefined}
                            onKeyDown={clickable ? (event) => {
                                if (event.key !== "Enter" && event.key !== " ") return;
                                event.preventDefault();
                                onFocus(spec.groupField || "label", selected ? null : row.label);
                            } : undefined}
                            className={`grid grid-cols-[minmax(90px,24%)_minmax(0,1fr)_auto] items-center gap-[8px] text-[11px]${clickable ? " cursor-pointer" : ""}`}
                            style={{ minHeight: "19px", opacity: dimmed ? 0.35 : 1 }}
                        >
                            <OverflowTooltip as="div" text={row.label} className="min-w-0 text-left font-mono text-[color:var(--ink)]">{row.label}</OverflowTooltip>
                            <div className="relative h-[15px]">
                                <div className="absolute inset-y-0 w-px bg-[color:var(--ink)] opacity-50" style={{ left: `${scale.zeroPercent}%` }} />
                                <div
                                    className="absolute top-[3px] h-[9px] min-w-px"
                                    style={{
                                        left: positive ? `${scale.zeroPercent}%` : `${scale.zeroPercent - width}%`,
                                        width: `${width}%`,
                                        background: sanitizeRenderColor(tone),
                                    }}
                                />
                            </div>
                            <span className="font-mono text-[10.5px] tabular-nums" style={{ color: tone }}>
                                {row.delta > 0 ? "+" : row.delta < 0 ? "-" : ""}{formatValue(Math.abs(row.delta), format)}
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="mt-[5px] text-[9.5px] text-[color:var(--muted-soft)]">All bars share one zero line and one proportional scale.</div>
        </div>
    );
}
